/**
 * localStorage adapter for session-level AutoCAD system variables.
 *
 * Patterns mirror `apps/web/src/app/i18n/index.ts` — any DOM/window access
 * is wrapped in try/catch so the code is safe to run under SSR or in
 * privacy-mode browsers where localStorage throws on read.
 *
 * Storage key: "edit-dwg.sysvars". Contents: JSON object `{ KEY: value }`
 * where each KEY is one of DYN_DEFAULTS (keys not in DYN_DEFAULTS are
 * filtered on load so a schema drift never crashes the store).
 */

import {
  DYN_DEFAULTS,
  isSysvarKey,
  isValidSysvarValue,
  type SysvarKey,
  type SysvarValues,
} from "./sysvarSchema";

const LOCAL_STORAGE_KEY = "edit-dwg.sysvars";
const PERSIST_DEBOUNCE_MS = 200;

/**
 * Reads persisted values and returns a partial record of only those
 * entries whose key is known and whose value passes validation. Missing
 * / invalid / unknown keys are dropped silently — callers fall back to
 * DYN_DEFAULTS for anything not returned.
 */
export function loadPersistedSysvars(): Partial<SysvarValues> {
  if (typeof window === "undefined") return {};
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  } catch {
    return {};
  }
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }
  const out: Partial<SysvarValues> = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (!isSysvarKey(k)) continue;
    if (typeof v !== "number") continue;
    if (!isValidSysvarValue(k, v)) continue;
    out[k] = v;
  }
  return out;
}

let pendingWrite: ReturnType<typeof setTimeout> | null = null;
let pendingValues: SysvarValues | null = null;

function doWrite(values: SysvarValues): void {
  if (typeof window === "undefined") return;
  try {
    // Only persist keys present in DYN_DEFAULTS so future schema additions
    // never leak into old payloads that then survive after rollback.
    const filtered: Record<string, number> = {};
    for (const key of Object.keys(DYN_DEFAULTS) as SysvarKey[]) {
      filtered[key] = values[key];
    }
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtered));
  } catch {
    // ignore — privacy mode or quota exceeded
  }
}

/**
 * Writes the full sysvar record to localStorage. Debounced 200 ms via a
 * module-level closure so rapid consecutive setVar calls (e.g. toggling
 * during a drag) collapse into a single write.
 *
 * Exposed test helper `flushPersistSysvars` forces the pending write to
 * run immediately — call it at the top of a test to reach deterministic
 * on-disk state without waiting for real timers.
 */
export function persistSysvars(values: SysvarValues): void {
  pendingValues = values;
  if (pendingWrite !== null) return;
  pendingWrite = setTimeout(() => {
    const toWrite = pendingValues;
    pendingWrite = null;
    pendingValues = null;
    if (toWrite) doWrite(toWrite);
  }, PERSIST_DEBOUNCE_MS);
}

/**
 * Test / teardown helper: commit the debounced payload synchronously.
 * Not exported from barrel — only tests should import this directly.
 */
export function flushPersistSysvars(): void {
  if (pendingWrite !== null) {
    clearTimeout(pendingWrite);
    pendingWrite = null;
  }
  if (pendingValues) {
    doWrite(pendingValues);
    pendingValues = null;
  }
}
